import { useState } from 'react';
import { useUrlState } from '@/hooks/useUrlState';
import {
  Stack,
  Table,
  TableContainer,
  TableHead,
  TableRow,
  TableCell,
  Typography,
} from '@mui/material';
import { Card } from '@liminal/web-ui/primitives/Card';
import { PageHeader } from '@liminal/web-ui/display/PageHeader';
import { Button } from '@liminal/web-ui/primitives/Button';
import { Pagination } from '@liminal/web-ui/primitives/Pagination';
import { StatusPill } from '@liminal/web-ui/primitives/StatusPill';
import { ConfirmationDialog } from '@liminal/web-ui/overlays/ConfirmationDialog';
import { FormErrorBanner } from '@liminal/web-ui/forms/FormErrorBanner';
import { DataTableBody } from '@liminal/web-ui/display/DataTableBody';
import { SortableTableCell } from '@liminal/web-ui/display/SortableTableCell';
import { useTableSort } from '@liminal/web-ui/primitives/useTableSort';
import { nimAnnouncementStatus } from '@/components/ui/statusHelpers';
import type { AdminNimAnnouncement } from '@/api/types';
import { useNimAnnouncements, useCancelNimAnnouncement } from '@/hooks/useNimAnnouncements';
import { formatOrderDate } from '@liminal/utils/date';
import { NimFirstMessageFormDialog } from '../components/NimFirstMessageFormDialog';
import { fontSizes } from '@liminal/brand/scale';
import { getErrorMessage } from '@/errors/messages';

const PAGE_SIZE = 50;
const COLUMN_COUNT = 7;

/**
 * Sorts the page on screen — these page by offset and the endpoint takes no
 * sort parameter.
 *
 * Audience and Delivered read the way their cells do: an all-users
 * announcement has no recipient snapshot, so both fall back rather than
 * ordering by a count that is not shown.
 */
const SORT_ACCESSORS = {
  content: (ann: AdminNimAnnouncement) => ann.content,
  audience: (ann: AdminNimAnnouncement) =>
    ann.audienceDescription ?? (ann.allUsers ? 'All users' : `${ann.recipientCount ?? 0} users`),
  deliveredCount: (ann: AdminNimAnnouncement) => ann.deliveredCount ?? 0,
  status: (ann: AdminNimAnnouncement) => nimAnnouncementStatus(ann).label,
  createdBy: (ann: AdminNimAnnouncement) => ann.createdBy,
  createdAt: (ann: AdminNimAnnouncement) => ann.createdAt,
};

const PAGE_DEFAULTS = { offset: 0 };

/**
 * Nim's first message: what Nim says first in each user's next new chat, once
 * per user, in place of its greeting. Stored as a Nim announcement (the API's
 * name for it).
 */
export function NimFirstMessagePage() {
  const [{ offset }, setParams] = useUrlState(PAGE_DEFAULTS);
  const [createOpen, setCreateOpen] = useState(false);
  // The id of the announcement awaiting a cancel confirmation, or null.
  const [cancelTarget, setCancelTarget] = useState<string | null>(null);
  const [cancelError, setCancelError] = useState('');

  const { data, isLoading, isError } = useNimAnnouncements({ limit: PAGE_SIZE, offset });
  const cancelMutation = useCancelNimAnnouncement();

  const total = data?.total ?? 0;

  // Newest first, as the endpoint returns them.
  const { rows: announcements, sort } = useTableSort(
    data?.announcements ?? [],
    SORT_ACCESSORS,
    'createdAt',
    'desc',
  );

  const handleCancelConfirm = async () => {
    if (!cancelTarget) return;
    setCancelError('');
    try {
      await cancelMutation.mutateAsync(cancelTarget);
      setCancelTarget(null);
    } catch (e) {
      setCancelError(getErrorMessage(e));
    }
  };

  return (
    <>
      <PageHeader
        title="First Message"
        autoTitleCase={false}
        actions={
          <Button variant="primary" startIcon="plus" onClick={() => setCreateOpen(true)}>
            New First Message
          </Button>
        }
      />
      <Stack spacing={3}>

      <Card variant="elevated" padding="none">
        <TableContainer>
          <Table size="small" sx={{ minWidth: 900 }}>
            <TableHead>
              <TableRow>
                <SortableTableCell column="content" sort={sort}>
                  Message
                </SortableTableCell>
                <SortableTableCell column="audience" sort={sort}>
                  Audience
                </SortableTableCell>
                <SortableTableCell column="deliveredCount" sort={sort}>
                  Delivered
                </SortableTableCell>
                <SortableTableCell column="status" sort={sort}>
                  Status
                </SortableTableCell>
                <SortableTableCell column="createdBy" sort={sort}>
                  Created By
                </SortableTableCell>
                <SortableTableCell column="createdAt" sort={sort}>
                  Created
                </SortableTableCell>
                <TableCell />
              </TableRow>
            </TableHead>
            <DataTableBody
              columns={COLUMN_COUNT}
              isLoading={isLoading}
              isError={isError}
              isEmpty={announcements.length === 0}
              loadingRows={8}
              emptyMessage="No first messages yet."
              errorMessage="Could not load first messages."
            >
              {announcements.map((ann) => (
                <TableRow key={ann.id} hover>
                  <TableCell sx={{ maxWidth: 320 }}>
                    <Typography variant="body2" noWrap title={ann.content}>
                      {ann.content}
                    </Typography>
                    {ann.buttonLabel && (
                      <Typography
                        variant="body2"
                        color="text.secondary"
                        noWrap
                        sx={{ fontSize: fontSizes.xs }}
                        title={ann.buttonUri}
                      >
                        Button: {ann.buttonLabel}
                      </Typography>
                    )}
                  </TableCell>
                  <TableCell sx={{ maxWidth: 200 }}>
                    <Typography
                      variant="body2"
                      noWrap
                      sx={{ fontSize: fontSizes.xs }}
                      title={ann.audienceDescription ?? ''}
                    >
                      {ann.audienceDescription ?? (ann.allUsers ? 'All users' : `${ann.recipientCount ?? 0} users`)}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2">
                      {/* All-users announcements have no snapshot to complete
                          against — they keep delivering to new signups. */}
                      {ann.allUsers ? (ann.deliveredCount ?? 0) : `${ann.deliveredCount ?? 0}/${ann.recipientCount ?? 0}`}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <StatusPill
                      label={nimAnnouncementStatus(ann).label}
                      tone={nimAnnouncementStatus(ann).variant}
                      size="sm"
                    />
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2" sx={{ fontSize: fontSizes.xs }}>
                      {ann.createdBy}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2" sx={{ whiteSpace: 'nowrap', fontSize: fontSizes.xs }}>
                      {formatOrderDate(ann.createdAt)}
                    </Typography>
                  </TableCell>
                  <TableCell align="right">
                    {!ann.cancelledAt && (ann.allUsers || (ann.deliveredCount ?? 0) < (ann.recipientCount ?? 0)) && (
                      <Button
                        variant="secondary"
                        size="sm"
                        disabled={cancelMutation.isPending}
                        onClick={() => {
                          setCancelError('');
                          setCancelTarget(ann.id);
                        }}
                      >
                        Cancel
                      </Button>
                    )}
                  </TableCell>
                </TableRow>
              ))}
            </DataTableBody>
          </Table>
        </TableContainer>
      </Card>

      <Pagination
        offset={offset}
        pageSize={PAGE_SIZE}
        count={announcements.length}
        total={total}
        onOffsetChange={(next) => setParams({ offset: next })}
        disabled={isLoading}
      />

      <NimFirstMessageFormDialog open={createOpen} onClose={() => setCreateOpen(false)} />

      <ConfirmationDialog
        open={cancelTarget !== null}
        title="Cancel this first message?"
        message="Users who have not received it yet will no longer get it. Already-delivered messages stay in their chats."
        confirmLabel="Cancel message"
        cancelLabel="Keep it"
        destructive
        loading={cancelMutation.isPending}
        onConfirm={() => void handleCancelConfirm()}
        onCancel={() => {
          setCancelTarget(null);
          setCancelError('');
        }}
        data-testid="cancel-nim-announcement"
      >
        <FormErrorBanner error={cancelError || undefined} />
      </ConfirmationDialog>
      </Stack>
    </>
  );
}
